import { useEffect, useMemo, useState } from 'react'
import { CATEGORY_PUBLICITY } from '../../domain/domainCodes'
import { useNavigate, useParams } from 'react-router-dom'
import { Clock, FileSpreadsheet, FolderTree, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ExcelImportModal from '../../components/ExcelImportModal'
import FileField from '../../components/FileField'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import { FormCols, FormSection, InfoNote, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import { EmptyState } from '../../components/States'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import QuestionFormDrawer from './QuestionFormDrawer'
import { getCategory, listCategoryQuestions, listLookups, saveCategory } from './contentService'
import { categoriesStrings as S, categoryWizard as W } from './strings'
import './CategoryFormPage.css'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  name: '',
  nameEn: '',
  description: '',
  descriptionEn: '',
  publicity: CATEGORY_PUBLICITY.PUBLIC,
  banner: null,
}

/* `fileAr` / `fileEn`, NOT `videoAr` / `videoEn`.

   `saveCategory` appends a chapter's videos as `chapter.fileAr` and `chapter.fileEn` — those
   exact names — and this held them under two the service never reads. Both files were picked,
   carried through the form and dropped on the floor at save: the chapter was created from its
   titles alone, with no video on disk, which is why its length and its file count came back
   empty. `lengthAr` / `lengthEn` are the durations in seconds, read off the file when it is
   chosen. */
const EMPTY_CHAPTER = { titleAr: '', titleEn: '', fileAr: null, fileEn: null, lengthAr: 0, lengthEn: 0 }

/**
 * How long an mp4 runs, in whole seconds, read from the file itself.
 *
 * The legacy does exactly this and nothing cleverer: it holds two hidden `<video>` elements,
 * points one at the chosen file and reads `.duration` off it (`getDuration` in
 * `_ManageCategoryModal.cshtml`), then posts the number alongside the upload. The server
 * never measures anything — on the import path it shells out to ffmpeg, but for a file picked
 * in the browser the browser already knows.
 *
 * Resolves to 0 rather than rejecting: a duration that cannot be read is worth less than the
 * upload it would otherwise block.
 */
const readDuration = (file) =>
  new Promise((resolve) => {
    if (!file) return resolve(0)

    const url = URL.createObjectURL(file)
    const probe = document.createElement('video')
    const done = (seconds) => {
      URL.revokeObjectURL(url)
      resolve(Number.isFinite(seconds) && seconds > 0 ? Math.trunc(seconds) : 0)
    }

    probe.preload = 'metadata'
    probe.onloadedmetadata = () => done(probe.duration)
    probe.onerror = () => done(0)
    probe.src = url
  })

/** Seconds as MM:SS — the same shape the server sends for a saved chapter. */
const formatDuration = (seconds) => {
  const total = Math.max(0, Math.trunc(Number(seconds) || 0))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/**
 * موضوع جديد / تعديل — Views/Shared/_ManageCategoryModal.
 *
 * The legacy is a three-step wizard and the redesign had only its FIRST step:
 * the six identity fields. Both of the other two were missing outright, which
 * is most of what the screen is for — a topic with no chapters and no questions
 * cannot become a course, so every topic created here had to be finished
 * somewhere else.
 *
 *   1  the topic      name, description and banner, in both languages
 *   2  the chapters   a title pair and an mp4 pair per chapter, added one at a
 *                     time to a list, exactly as the legacy's dropzones do
 *   3  the questions  the topic's bank, with the single-question editor and the
 *                     Excel import the legacy puts behind the same table
 *
 * Steps 2 and 3 work on a topic that has to EXIST to hold them, which is why the
 * legacy saves between steps and why the rail here is disabled past step 1
 * until the topic is named.
 */
export default function CategoryFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack('/categories')
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [chapters, setChapters] = useState([])
  const [draft, setDraft] = useState(EMPTY_CHAPTER)
  const [questions, setQuestions] = useState([])
  const [questionOpen, setQuestionOpen] = useState(false)
  const [excelOpen, setExcelOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  /** Chapters removed from the list; the server needs their ids to actually drop them. */
  const [deletedChapters, setDeletedChapters] = useState([])
  /** The topic and difficulty options the question form offers. */
  const [lookups, setLookups] = useState({ categories: [], difficulties: [] })

  useEffect(() => {
    if (!id) return
    let alive = true
    Promise.all([getCategory(id), listCategoryQuestions(id)]).then(([category, rows]) => {
      if (!alive) return
      setValues({ ...EMPTY, ...category })
      setChapters(category?.chapters ?? [])
      setQuestions(rows)
    })
    return () => {
      alive = false
    }
  }, [id])

  useEffect(() => {
    let alive = true
    listLookups()
      .then((options) => alive && setLookups(options))
      .catch(() => alive && setLookups({ categories: [], difficulties: [] }))
    return () => {
      alive = false
    }
  }, [])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }
  const setDraftField = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }))

  /* Choosing a video sets the file AND its length, so the chapter carries a duration from
     the moment it is added rather than showing 00:00 until someone re-saves it. */
  const setDraftVideo = (fileKey, lengthKey) => async (file) => {
    setDraft((previous) => ({ ...previous, [fileKey]: file, [lengthKey]: 0 }))
    const seconds = await readDuration(file)
    setDraft((previous) => (previous[fileKey] === file ? { ...previous, [lengthKey]: seconds } : previous))
  }

  const steps = useMemo(
    () => [
      { id: 'topic', label: W.steps.topic },
      { id: 'chapters', label: W.steps.chapters },
      { id: 'questions', label: W.steps.questions },
    ],
    [],
  )

  const validateStep = (index) => {
    if (index !== 0) return true
    const next = {}
    if (!values.name.trim()) next.name = S.form.required
    if (!values.nameEn.trim()) next.nameEn = S.form.required
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const addChapter = () => {
    if (!draft.titleAr.trim() || !draft.titleEn.trim()) return
    /* NEGATIVE ids for chapters that do not exist yet. The handler reads `Id <= 0` as
       "create this one", and looks for its video under `{Id}-ar.mp4` — so the id has to be
       decided here, before the upload, and has to be one the server will not mistake for
       an existing chapter. */
    setChapters((previous) => [
      ...previous,
      { ...draft, id: -(previous.filter((chapter) => chapter.id < 0).length + 1) },
    ])
    setDraft(EMPTY_CHAPTER)
  }

  /**
   * One post carries the whole wizard.
   *
   * `questions` used to be left out of the call entirely, so anything added or edited on
   * the third step was discarded on save without a word. `deletedChapters` matters too:
   * dropping a chapter from the list is only a removal once the server is told which id
   * went.
   */
  const submit = async () => {
    setSaving(true)
    const outcome = await saveCategory({
      ...values,
      chapters,
      questions,
      deletedChapters,
    })
    setSaving(false)

    if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)

    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/categories')
  }

  return (
    <>
      <FormPage
        icon={FolderTree}
        title={id ? S.form.editTitle : S.actions.create}
        subtitle={S.title}
        steps={steps}
        validateStep={validateStep}
        onBack={goBack}
        onSubmit={submit}
        submitLabel={S.form.save}
        submitting={saving}
      >
        {({ step }) => {
          if (step === 0) {
            return (
              <FormSection>
                <FormCols>
                  <FormRow label={S.form.nameAr} error={errors.name}>
                    {(fid) => (
                      <TextField
                        id={fid}
                        value={values.name}
                        onChange={set('name')}
                        invalid={!!errors.name}
                        autoComplete="off"
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.nameEn} error={errors.nameEn}>
                    {(fid) => (
                      <TextField
                        id={fid}
                        dir="ltr"
                        value={values.nameEn}
                        onChange={set('nameEn')}
                        invalid={!!errors.nameEn}
                        autoComplete="off"
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.descriptionAr}>
                    {(fid) => (
                      <Textarea
                        id={fid}
                        rows={5}
                        value={values.description}
                        onChange={set('description')}
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.descriptionEn}>
                    {(fid) => (
                      <Textarea
                        id={fid}
                        dir="ltr"
                        rows={5}
                        value={values.descriptionEn}
                        onChange={set('descriptionEn')}
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.publicity}>
                    {(fid) => (
                      <Select
                        id={fid}
                        value={values.publicity}
                        onChange={(value) => set('publicity')(Number(value))}
                        options={[
                          { value: 0, label: S.form.public },
                          { value: 1, label: S.form.private },
                        ]}
                      />
                    )}
                  </FormRow>
                  <FormRow label={S.form.banner}>
                    {(fid) => (
                      <FileField id={fid} accept="image/png" onChange={set('banner')} />
                    )}
                  </FormRow>
                </FormCols>
              </FormSection>
            )
          }

          if (step === 1) {
            return (
              <FormSection>
                {/* The legacy's two dropzones and their title pair, as one add
                    row: a chapter is not saved until both languages have a file,
                    so the four controls belong together rather than as four
                    fields you fill and a button you hunt for. */}
                <div className="catwiz__adder">
                  <FormCols>
                    <FormRow label={W.chapters.titleAr}>
                      {(fid) => (
                        <TextField
                          id={fid}
                          value={draft.titleAr}
                          onChange={setDraftField('titleAr')}
                          autoComplete="off"
                        />
                      )}
                    </FormRow>
                    <FormRow label={W.chapters.titleEn}>
                      {(fid) => (
                        <TextField
                          id={fid}
                          dir="ltr"
                          value={draft.titleEn}
                          onChange={setDraftField('titleEn')}
                          autoComplete="off"
                        />
                      )}
                    </FormRow>
                    <FormRow
                      label={W.chapters.videoAr}
                      hint={draft.fileAr ? formatDuration(draft.lengthAr) : undefined}
                    >
                      {(fid) => (
                        <FileField
                          id={fid}
                          accept=".mp4"
                          onChange={setDraftVideo('fileAr', 'lengthAr')}
                        />
                      )}
                    </FormRow>
                    <FormRow
                      label={W.chapters.videoEn}
                      hint={draft.fileEn ? formatDuration(draft.lengthEn) : undefined}
                    >
                      {(fid) => (
                        <FileField
                          id={fid}
                          accept=".mp4"
                          onChange={setDraftVideo('fileEn', 'lengthEn')}
                        />
                      )}
                    </FormRow>
                  </FormCols>

                  <div className="catwiz__adder-moves">
                    <Button
                      type="button"
                      onClick={addChapter}
                      disabled={!draft.titleAr.trim() || !draft.titleEn.trim()}
                    >
                      <Plus size={16} strokeWidth={2} />
                      {W.chapters.add}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setDraft(EMPTY_CHAPTER)}>
                      {W.chapters.reset}
                    </Button>
                  </div>
                </div>

                {chapters.length === 0 ? (
                  <EmptyState title={W.chapters.empty} />
                ) : (
                  <ol className="catwiz__list">
                    {chapters.map((chapter, index) => (
                      <li key={chapter.id ?? index} className="catwiz__row">
                        <span className="catwiz__index tabular">{index + 1}</span>
                        <span className="catwiz__row-titles">
                          <bdi className="catwiz__row-ar">{chapter.titleAr ?? chapter.name}</bdi>
                          <bdi className="catwiz__row-en" dir="ltr">
                            {chapter.titleEn ?? chapter.nameEn}
                          </bdi>
                        </span>
                        {/* THE TWO LENGTHS, as the legacy's chapter table carries them — one
                            column per language between the two names. A chapter is a pair of
                            videos, and the only thing on this row that says whether both
                            arrived is how long each one runs. */}
                        <span className="catwiz__row-lengths tabular">
                          <span className="catwiz__len">
                            <Clock size={13} strokeWidth={1.75} aria-hidden="true" />
                            {chapter.durationAr || formatDuration(chapter.lengthAr)}
                          </span>
                          <span className="catwiz__len" dir="ltr">
                            <Clock size={13} strokeWidth={1.75} aria-hidden="true" />
                            {chapter.durationEn || formatDuration(chapter.lengthEn)}
                          </span>
                        </span>
                        <IconButton
                          icon={Trash2}
                          label={W.chapters.remove}
                          tone="danger"
                          onClick={() => {
                            /* A chapter that was never saved has no id to delete; one that
                               was has to be named or the server keeps it. */
                            /* Only a saved chapter can be deleted; a negative id never
                               reached the server, so naming it would be a lie. */
                            if (chapter.id > 0) {
                              setDeletedChapters((previous) => [...previous, chapter.id])
                            }
                            setChapters((previous) => previous.filter((_, i) => i !== index))
                          }}
                        />
                      </li>
                    ))}
                  </ol>
                )}
              </FormSection>
            )
          }

          return (
            <FormSection>
              <InfoNote>
                <bdi>{`${questions.length} ${W.questions.count}`}</bdi>
              </InfoNote>

              <div className="catwiz__adder-moves">
                <Button type="button" onClick={() => setQuestionOpen(true)}>
                  <Plus size={16} strokeWidth={2} />
                  {W.questions.add}
                </Button>
                <Button type="button" variant="soft" onClick={() => setExcelOpen(true)}>
                  <FileSpreadsheet size={16} strokeWidth={1.75} />
                  {W.questions.fromExcel}
                </Button>
              </div>

              {questions.length === 0 ? (
                <EmptyState title={W.questions.empty} />
              ) : (
                <ol className="catwiz__list">
                  {questions.map((question, index) => (
                    <li key={question.id ?? index} className="catwiz__row">
                      <span className="catwiz__index tabular">{index + 1}</span>
                      <bdi className="catwiz__row-ar">{question.text}</bdi>
                    </li>
                  ))}
                </ol>
              )}
            </FormSection>
          )
        }}
      </FormPage>

      {/* Both lists are required: the drawer feeds them straight to a `Select`, and an
          undefined `options` throws while rendering — which took the whole page down. */}
      <QuestionFormDrawer
        open={questionOpen}
        question={null}
        categories={lookups.categories}
        levels={lookups.difficulties}
        onClose={() => setQuestionOpen(false)}
        onSave={(question) => {
          /* NEGATIVE ids, for the same reason chapters use them: the handler reads
             `QuestionId > 0` as "update that one". A timestamp is neither — and it does not
             even survive the trip, because `QuestionId` is an `int?` and `Date.now()` is
             larger than `int.MaxValue`, so deserialising the whole request THREW and the
             save came back as a bare "an error occurred". */
          setQuestions((previous) => [
            ...previous,
            { ...question, id: -(previous.filter((item) => item.id < 0).length + 1) },
          ])
          setQuestionOpen(false)
        }}
      />

      <ExcelImportModal
        open={excelOpen}
        title={W.questions.fromExcel}
        onClose={() => setExcelOpen(false)}
        onSubmit={() => setExcelOpen(false)}
      />
    </>
  )
}
